export const AdminRole = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  VIEWER: 'viewer',
} as const

export type AdminRole = (typeof AdminRole)[keyof typeof AdminRole]

export interface AdminUser {
  id: string
  email: string
  name: string | null
  role: AdminRole
  lastLoginAt: string | null
  createdAt: string
}

export interface AlertSeverity {
  level: 'warning' | 'critical' | 'info'
}

export interface Alert {
  id: string
  rule: string
  severity: 'warning' | 'critical' | 'info'
  message: string
  firedAt: string
  resolvedAt: string | null
}

export interface BouncingDomain {
  domain: string
  bounceRate: number
  totalSent: number
  totalBounced: number
}

export type AccountOverrides = {
  rate_limit?: number
  email_limit?: number
  overage_policy?: 'allow' | 'block' | 'warn'
  ramp_bypass?: boolean
  send_rate?: number
  max_recipients?: number
  force_transport?: string
  notes?: string
  expires_at?: string
}

export type Account = {
  id: string
  user_email: string
  user_name: string | null
  tier: string
  status: string
  sending_paused: boolean
  pause_reason: string | null
  emails_sent_this_period: number
  email_credits: number
  overrides: AccountOverrides | null
  domain_count: number
  api_key_count: number
  created_at: string
  last_active_at: string | null
}

export type AccountListResponse = {
  data: Account[]
  total: number
  page: number
  per_page: number
}

export type AccountDetailResponse = Account & {
  domains: AccountDomain[]
  audit_log: AuditEntry[]
  overage_count: number
  period_start: string | null
  period_end: string | null
}

export type AccountDomain = {
  id: string
  domain: string
  status: string
  spf: boolean
  dkim: boolean
  mx: boolean
  dmarc: boolean
  created_at: string
}

export type AuditEntry = {
  id: string
  admin_email: string
  action: string
  details: Record<string, unknown> | null
  created_at: string
}

export type BillingSummary = {
  mrr: number
  accounts_by_tier: Record<string, { count: number; emails: number; revenue: number }>
  total_emails: number
  override_count: number
}

export type OverageAccount = {
  id: string
  user_email: string
  tier: string
  emails_sent: number
  limit: number
  percent_used: number
  overage_count: number
}

export type OveragesResponse = {
  approaching: OverageAccount[]
  in_overage: OverageAccount[]
}

export type EngineHealth = {
  status: 'healthy' | 'degraded'
  queue_depth: number
  uptime_seconds: number
  version: string
}

export type QueueStats = {
  total: number
  by_domain: Record<string, number>
}

export type PrometheusMetric = {
  name: string
  type: 'counter' | 'gauge' | 'histogram'
  value: number
  labels?: Record<string, string>
}

export type EngineMetrics = {
  messages_accepted: number
  messages_delivered: number
  messages_bounced: number
  messages_deferred: number
  delivery_duration_p50: number
  delivery_duration_p99: number
  queue_depth: number
  connections_active: number
  connections_idle: number
  memory_bytes: number
  cpu_seconds: number
  dkim_sign_p50: number
  compose_p50: number
  queue_write_p50: number
}

export type DomainRecord = {
  id: string
  account_id: string
  account_email: string
  domain: string
  status: 'pending' | 'verified' | 'failed'
  spf_status: 'pass' | 'fail' | 'pending'
  dkim_status: 'pass' | 'fail' | 'pending'
  mx_status: 'pass' | 'fail' | 'pending'
  dmarc_status: 'pass' | 'fail' | 'pending'
  created_at: string
  verified_at: string | null
}

export type OverviewStats = {
  emails_today: number
  delivery_rate: number
  bounce_rate: number
  queue_depth: number
  uptime_seconds: number
  active_accounts: number
}

export type VolumeDataPoint = {
  hour: string
  sent: number
  delivered: number
  bounced: number
}

export type AnalyticsResponse = {
  sent: number
  delivered: number
  bounced: number
  delivery_rate: number
  bounce_rate: number
}

export type AnalyticsBounceResponse = {
  domains: BouncingDomain[]
}

export type EmailRecord = {
  id: string
  account_id: string
  account_email: string
  from_address: string
  to_addresses: string[]
  subject: string
  status: string
  transport: string
  domain: string
  tags: Record<string, string>
  created_at: string
}

export type EmailEvent = {
  id: string
  type: string
  recipient_email: string
  bounce_type: string | null
  transport: string
  data: Record<string, unknown>
  timestamp: string
}

export type EmailDetail = EmailRecord & {
  events: EmailEvent[]
  message_id: string
}

export type BuilderApplication = {
  id: string
  name: string
  email: string
  type: 'student' | 'startup'
  company_or_school: string | null
  location: string | null
  website: string | null
  github: string | null
  description: string
  use_case: string | null
  expected_volume: string | null
  status: 'pending' | 'approved' | 'denied'
  admin_notes: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  created_at: string
}

export type BuilderApplicationListResponse = {
  data: BuilderApplication[]
  total: number
  page: number
  per_page: number
}

export type PaginatedResponse<T> = {
  data: T[]
  total: number
  page: number
  per_page: number
}

export type StagingHealthResponse = {
  status: 'ok'
  uptime_seconds: number
  version: string
}

export type StagingDeepHealthResponse = {
  status: 'ok'
  checks: {
    postgres: { status: 'ok' | 'error'; latency_ms: number }
    valkey: { status: 'ok' | 'error'; latency_ms: number }
  }
}

export type InfraHostMetrics = {
  hostname: string
  host_type: 'control_plane' | 'smtp'
  cpu_percent: number
  memory_percent: number
  memory_used_mb: number
  memory_total_mb: number
  disk_percent: number
  disk_used_gb: number
  disk_total_gb: number
  load_1: number
  load_5: number
  load_15: number
  network_rx_mbps: number
  network_tx_mbps: number
  top_processes: InfraProcess[]
  collected_at: string
}

export type InfraProcess = {
  pid: number
  name: string
  cpu_percent: number
  memory_mb: number
  state: string
}

export type InfraSnapshot = {
  hosts: InfraHostMetrics[]
  collected_at: string
}

export type InfraHistoryPoint = {
  time: string
  cpu_percent: number
  memory_percent: number
  disk_percent: number
  network_rx_mbps: number
  network_tx_mbps: number
  load_1: number
}

export type InfraHistoryResponse = {
  host: string
  period: string
  points: InfraHistoryPoint[]
}

export type InfraThreshold = {
  metric: string
  warning: number
  critical: number
  sustained_readings: number
  enabled: boolean
}

export type InfraThresholdsResponse = {
  thresholds: InfraThreshold[]
}

export type {
  AdminUserRecord,
  AlertHistoryEntry,
  AlertRule,
  BounceAnalyticsResponse,
  BounceCategory,
  FullAuditEntry,
  IspStats,
  RecentBounce,
  SettingsResponse,
  SlackSettings,
} from './types-analytics'
